import { useTable } from "react-table";
import { useContext, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  defaultDateOnlyDisplay,
  formatCurrency,
  truncateString,
} from "../../../helpers";
import primaryComponents from "../../primaryComponents";
import { useQuery } from "react-query";
import { getExpenseDetails } from "../../../services/expenses";
import useGetPermission from "../../../hooks/useGetPermission";
import { pathConstants } from "../../../routes/pathContants";
import useExpenseCategories from "../../../pages/appPages/Expenses/ExpenseCategories/useExpenseCategories";
import { ToastContext } from "../../../hooks/context";
import { useUpdateExpenseCategory } from "../../../hooks/api/mutations/expenses";

const useExpensesTable = ({
  expenses,
  isViaSellMode = false,
  isRecurringTab = false,
}) => {
  const { isFetchingCategories, expenseCategories } = useExpenseCategories();
  const updateExpenseCategory = useUpdateExpenseCategory();
  const isUpdatingCategory = updateExpenseCategory.isLoading;
  const triggerToast = useContext(ToastContext);
  const [expenseView, setExpenseView] = useState({
    id: null,
    type: null,
  });
  const { isPermitted } = useGetPermission({
    permissionParentKey: "Expense",
    permissionChildKey: "can_approve_expense",
  });
  const statusMap = {
    APPROVED: "success--squared",
    DISBURSED: "success--squared",
    PENDING: "pending--squared",
    DECLINED: "failed--text",
  };

  const columns = useMemo(
    () => [
      {
        Header: "Expense Date",
        accessor: "expense_date",
        Cell: ({ cell: { value } }) =>
          value ? defaultDateOnlyDisplay(value) : "N/A",
      },
      {
        Header: "Expense Name",
        accessor: "name",
        Cell: ({ row, cell: { value } }) => (
          <div
            className="d-flex align-items-center flex-wrap"
            style={{ gap: "6px" }}
          >
            {!isViaSellMode ? (
              <Link
                className="purple--text smallest-text-size"
                to={pathConstants.EXPENSES_DETAILS({
                  id: row?.original?.id,
                  store_id: row?.original?.store?.id,
                })}
                style={{
                  textDecoration: "underline",
                }}
              >
                {value}
              </Link>
            ) : (
              value
            )}
            {row?.original?.is_recurring && (
              <span
                className="gray--primary"
                title={`Recurring expense${
                  row?.original?.recurrence_frequency
                    ? ` (${row.original.recurrence_frequency})`
                    : ""
                }`}
              >
                <FontAwesomeIcon icon="redo-alt" className="mr-1" />
                Recurring
              </span>
            )}
            {row?.original?.source === "recurring" && (
              <span
                className="gray--squared"
                title="Auto-generated from a recurring expense"
              >
                System-generated
              </span>
            )}
          </div>
        ),
      },
      {
        Header: "Category",
        accessor: "category",
        Cell: ({ cell: { value }, row }) => {
          if (isFetchingCategories) return null;

          return (
            <div style={{ width: "130px" }} className={"selectPropMid"}>
              <primaryComponents.SelectField
                options={expenseCategories}
                giveControl={false}
                value={
                  row?.original?.category?.id
                    ? {
                        label: row?.original?.category?.name,
                        value: row?.original?.category?.id,
                      }
                    : null
                }
                placeholder={"Select Category"}
                floatError={false}
                nameKey={"name"}
                idKey={"id"}
                onChange={(selectedCategory) => {
                  const categoryId = selectedCategory?.id || selectedCategory;
                  updateExpenseCategory.mutate(
                    {
                      expenseId: row.original.id,
                      categoryId,
                    },
                    {
                      onSuccess: () =>
                        triggerToast(
                          "Category updated successfully",
                          "success",
                        ),
                    },
                  );
                }}
                isLoading={isUpdatingCategory}
              />
            </div>
          );
        },
      },

      {
        Header: "Amount",
        accessor: "amount",
        Cell: ({ cell }) =>
          formatCurrency({
            value: cell.value,
            currencyCode: cell?.row?.original?.store?.currency_code,
          }),
      },
      ...(isRecurringTab
        ? []
        : [
            {
              Header: "Initiated By",
              accessor: "initiated_by",
              Cell: ({ cell: { value } }) =>
                value ? truncateString(value?.first_name, 15) : "N/A",
            },
          ]),
      {
        Header: "Approved By",
        accessor: "reviewed_by",
        Cell: ({ cell: { value } }) =>
          value ? truncateString(value?.first_name, 15) : "N/A",
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: ({ cell: { value } }) => (
          <span className={`${statusMap[value]} text-capitalize`}>{value}</span>
        ),
      },
      ...(isRecurringTab
        ? [
            {
              Header: "Recurrence Status",
              accessor: "recurrence_cancelled_at",
              Cell: ({ cell: { value } }) => (
                <span className={value ? "error--squared" : "success--squared"}>
                  {value ? "Cancelled" : "Active"}
                </span>
              ),
            },
          ]
        : []),
      {
        Header: "Action",
        Cell: ({ row }) => (
          <>
            {isPermitted ? (
              <primaryComponents.Button
                onClick={() => {
                  setExpenseView({
                    id: row?.original?.id,
                    type: "REVIEW",
                  });
                }}
                classNames={"btn btn--primary smallBtn"}
                isDisabled={row?.original?.status !== "PENDING"}
              >
                Review
              </primaryComponents.Button>
            ) : (
              <primaryComponents.Button
                onClick={() => {
                  setExpenseView({
                    id: row?.original?.id,
                    type: "UPDATE",
                  });
                }}
                classNames={"btn btn--primary smallBtn"}
                isDisabled={row?.original?.status !== "PENDING"}
              >
                Update
              </primaryComponents.Button>
            )}
          </>
        ),
      },
    ],
    [expenseCategories, isRecurringTab],
  );

  const { data: expenseDetails, isLoading: isFetchingExpenseDetails } =
    useQuery(
      ["getExpenseDetails", expenseView?.id],
      () =>
        getExpenseDetails(expenseView?.id).then((res) => {
          return res.data?.data;
        }),
      {
        enabled: expenseView?.id !== null,
      },
    );

  const instance = useTable({ columns, data: expenses });
  return {
    instance,
    expenseView,
    isFetchingExpenseDetails,
    expenseDetails: expenseDetails || {},
    setExpenseView,
  };
};

export default useExpensesTable;
